import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EntityId } from '@shared/core/types';
import { getErrorString } from '@shared/core/utils';
import {
  EducationService,
  EducationServiceVacation,
  EducationServiceVacationCreateRequest,
  EducationServiceVacationUpdateRequest,
} from '@crm/shared/persons/types';
import { NotifierService } from '@shared/core';
import { VacationManagementApiService } from '@crm/shared/persons/services/vacation-management-api.service';
import { registerTranslationsManually } from '@skyeng/libs/shared/i18n';
import { TranslocoService } from '@ngneat/transloco';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';

@DecorateUntilDestroy()
@Component({
  selector: 'crm-shared-persons-vacation-management',
  templateUrl: './vacation-management.component.html',
  styleUrls: ['./vacation-management.component.scss'],
})
export class VacationManagementComponent implements OnInit, OnDestroy {
  @Input() educationService: EducationService;
  @Input() personId: EntityId;
  @Input() currentOrFutureVacation: EducationServiceVacation | null;

  @Output() sentOnVacation = new EventEmitter<void>();
  @Output() changedVacationDates = new EventEmitter<void>();
  @Output() cancelledVacation = new EventEmitter<void>();
  @Output() returnedFromVacation = new EventEmitter<void>();

  constructor(
    private vacationManagementApi: VacationManagementApiService,
    private notifier: NotifierService,
    private translocoService: TranslocoService,
  ) {
    registerTranslationsManually(
      'personsVacationManagement',
      translocoService,
      import.meta.webpackContext!('./translation'),
    );
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  sendOnVacation(data: EducationServiceVacationCreateRequest): void {
    this.vacationManagementApi
      .sendOnVacation(this.educationService.id, data)
      .pipe(takeUntilDestroyed(this))
      .subscribe(
        () => {
          this.notifier.show(this.translocoService.translate('personsVacationManagement.studentOnVacation'));
          this.sentOnVacation.emit();
        },
        error =>
          this.notifier.error(
            this.translocoService.translate('personsVacationManagement.studentOnVacationError', {
              error: getErrorString(error),
            }),
          ),
      );
  }

  changeVacationDates(data: EducationServiceVacationUpdateRequest): void {
    this.vacationManagementApi
      .changeVacationDates(this.educationService.id, data)
      .pipe(takeUntilDestroyed(this))
      .subscribe(
        () => {
          this.notifier.show(this.translocoService.translate('personsVacationManagement.vacationDatesChanged'));
          this.changedVacationDates.emit();
        },
        error =>
          this.notifier.error(
            this.translocoService.translate('personsVacationManagement.vacationDatesChangedError', {
              error: getErrorString(error),
            }),
          ),
      );
  }

  cancelVacation(): void {
    this.vacationManagementApi
      .cancelVacation(this.educationService.id)
      .pipe(takeUntilDestroyed(this))
      .subscribe(
        () => {
          this.notifier.show(this.translocoService.translate('personsVacationManagement.vacationCanceled'));
          this.cancelledVacation.emit();
        },
        error =>
          this.notifier.error(
            this.translocoService.translate('personsVacationManagement.vacationCanceledError', {
              error: getErrorString(error),
            }),
          ),
      );
  }

  returnFromVacation(): void {
    this.vacationManagementApi
      .returnFromVacation(this.educationService.id)
      .pipe(takeUntilDestroyed(this))
      .subscribe(
        () => {
          this.notifier.show(this.translocoService.translate('personsVacationManagement.studentReturned'));
          this.returnedFromVacation.emit();
        },
        error =>
          this.notifier.error(
            this.translocoService.translate('personsVacationManagement.studentReturnedError', {
              error: getErrorString(error),
            }),
          ),
      );
  }
}
